import React from "react";
import { useParams } from "react-router-dom";
import useApplicantDetails from "../../hooks/applications/useApplicantDetails";
import { Container, Grid } from "@mui/material";
import PersonalInformation from "../../components/application/applicantProfile/PersonalInformation";
import LoadingCircle from "../../components/general/LoadingCircle";
import Address from "../../components/application/applicantProfile/Address";
import StudyDetails from "../../components/application/applicantProfile/StudyDetails";
import Apprenticeship from "../../components/application/applicantProfile/Apprenticeship";
import Occupation from "../../components/application/applicantProfile/Occupation";
import HiwiStudentJobs from "../../components/application/applicantProfile/HiwiStudentJobs";
import Internships from "../../components/application/applicantProfile/Internships";
import VoluntaryActivitesSchool from "../../components/application/applicantProfile/VoluntaryActivitesSchool";
import VoluntaryActivitesStudy from "../../components/application/applicantProfile/VoluntaryActivitesStudy";
import Languages from "../../components/application/applicantProfile/Languages";
import ItSkills from "../../components/application/applicantProfile/ItSkills";
import Hobbies from "../../components/application/applicantProfile/Hobbies";
import TimeInvestment from "../../components/application/applicantProfile/TimeInvestment";
import Motivation from "../../components/application/applicantProfile/Motivation";
import SelfAssessment from "../../components/application/applicantProfile/SelfAssessment";
import Availability from "../../components/application/applicantProfile/Availability";

/**
 * This page displays the profile of an applicant.
 * @returns The profile of an applicant.
 */
const ApplicantProfile = () => {
  const applicantID = Number(useParams<{ id: string }>().id);
  const { applicantDetails, applicantImage } = useApplicantDetails(applicantID);

  if (!applicantDetails) {
    return <LoadingCircle />;
  }

  return (
    <Container>
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <PersonalInformation {...applicantDetails} image={applicantImage} />
        </Grid>
        <Grid
          item
          xs={12}
          sm={8}
          rowSpacing={3}
          container
          sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between" }}
        >
          <Grid item>
            <Address {...applicantDetails} />
          </Grid>
          <Grid item>
            <StudyDetails {...applicantDetails} />
          </Grid>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Apprenticeship {...applicantDetails} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Occupation {...applicantDetails} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <HiwiStudentJobs hiwiStudentJobs={applicantDetails.hiwiStudentJob} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Internships internships={applicantDetails.internship} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VoluntaryActivitesSchool voluntarySchool={applicantDetails.voluntarySchool} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <VoluntaryActivitesStudy voluntaryStudy={applicantDetails.voluntaryStudy} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Languages languages={applicantDetails.languages} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ItSkills itSkills={applicantDetails.itSkills} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Hobbies hobbies={applicantDetails.hobbies} />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TimeInvestment timeInvestment={applicantDetails.timeInvestment} />
        </Grid>
        <Grid item xs={12}>
          <Motivation motivation={applicantDetails.motivation} />
        </Grid>
        <Grid item xs={12}>
          <SelfAssessment
            selfAssessments={[
              { name: "selfAssessment1", value: applicantDetails.selfAssessment1 },
              { name: "selfAssessment2", value: applicantDetails.selfAssessment2 },
              { name: "selfAssessment3", value: applicantDetails.selfAssessment3 },
              { name: "selfAssessment4", value: applicantDetails.selfAssessment4 },
              { name: "selfAssessment5", value: applicantDetails.selfAssessment5 },
              { name: "selfAssessment6", value: applicantDetails.selfAssessment6 },
              { name: "selfAssessment7", value: applicantDetails.selfAssessment7 },
              { name: "selfAssessment8", value: applicantDetails.selfAssessment8 },
            ]}
          />
        </Grid>
        <Grid item xs={12}>
          <Availability
            workingWeekend={applicantDetails.workingWeekend}
            availabilitySelectionWeekend={applicantDetails.availabilitySelectionWeekend}
          />
        </Grid>
      </Grid>
    </Container>
  );
};

export default ApplicantProfile;
